import React, { useEffect } from 'react';
import { SaveOutlined } from '@ant-design/icons';
import apiConfig from '@constants/apiConfig';
import { doneType } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useBasicForm from '@hooks/useBasicForm';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, NumericField, SelectField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { validatePermission } from '@utils';
import { Button, Col, Flex, Form, Row, Switch } from 'antd';

const KitplaySettingForm = ({ formId, actions, dataDetail, onSubmit, isChanged, setIsChangedFormValues, isEditing, loading }) => {
    const translate = useTranslate();
    const doneTypeValues = translate.formatKeys(doneType, ['label']);
    const { permissions } = useAuth();

    const hasPermission = validatePermission([apiConfig.kitplayDevice.updateSettings.baseURL], permissions);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const handleSubmit = (values) => {
        let currentSettings = {};
        try {
            currentSettings = dataDetail?.settings ? JSON.parse(dataDetail.settings) : {};
        } catch {
            currentSettings = {};
        }

        const finalValues = {
            settings: JSON.stringify({
                kitplay: {
                    time_delete_archive_order: values.timeDeleteArchiveOrder ? Number(values.timeDeleteArchiveOrder) : null,
                    done_type: values.doneType ? Number(values.doneType) : null,
                    push_to_mpos_inhouse: values.pushToMposInhouse ? true : false,
                    push_to_mpos_imbiss: values.pushToMposImbiss ? true : false,
                    push_to_mpos_takeaway: values.pushToMposTakeaway ? true : false,
                    push_to_mpos_delivery: values.pushToMposDelivery ? true : false,
                },
                pickup_display: currentSettings.pickup_display ?? {},
            }),
        };

        return mixinFuncs.handleSubmit(finalValues);
    };

    useEffect(() => {
        if (dataDetail && isEditing) {
            let parsedSettings = {};
            try {
                parsedSettings = dataDetail.settings ? JSON.parse(dataDetail.settings) : {};
            } catch {
                parsedSettings = {};
            }

            const kitplay = parsedSettings.kitplay ?? {};

            form.setFieldsValue({
                ...dataDetail,
                timeDeleteArchiveOrder: kitplay.time_delete_archive_order ?? null,
                doneType: kitplay.done_type ?? null,
                pushToMposInhouse: kitplay.push_to_mpos_inhouse ?? false,
                pushToMposImbiss: kitplay.push_to_mpos_imbiss ?? false,
                pushToMposTakeaway: kitplay.push_to_mpos_takeaway ?? false,
                pushToMposDelivery: kitplay.push_to_mpos_delivery ?? false,
            });
        } else {
            form.setFieldsValue({
                pushToMposInhouse: false,
                pushToMposImbiss: false,
                pushToMposTakeaway: false,
                pushToMposDelivery: false,
            });
        }
    }, [dataDetail, isEditing]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <div style={{ paddingTop: 16 }}>
                <Row gutter={[16, 8]}>
                    <Col span={12}>
                        <NumericField
                            label={translate.formatMessage(commonMessage.timeDeleteArchiveOrder) + ` (${translate.formatMessage(commonMessage.minute)})`}
                            name="timeDeleteArchiveOrder"
                            formatter={(v) => v}
                            parser={(v) => v}
                            rules={[{ pattern: /^[0-9]+$/, message: '' }]}
                            width="100%"
                            disabled={!hasPermission}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            label={translate.formatMessage(commonMessage.doneType)}
                            name="doneType"
                            options={doneTypeValues}
                            disabled={!hasPermission}
                        />
                    </Col>

                    <Col span={24}>
                        <fieldset style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '20px',
                        }}>
                            <legend style={{
                                width: 'auto',
                                padding: '0 8px',
                                fontSize: '14px',
                                color: 'rgba(0, 0, 0, 0.45)',
                                marginBottom: 0,
                                borderBottom: 'none',
                            }}>
                                {translate.formatMessage(commonMessage.pushToMpos)}
                            </legend>

                            <Row gutter={16}>
                                <Col span={6}>
                                    <Flex align="center" gap={12}>
                                        <Form.Item
                                            name="pushToMposInhouse"
                                            valuePropName="checked"
                                            noStyle
                                            required
                                        >
                                            <Switch
                                                disabled={!hasPermission}
                                                onChange={(value) => form.setFieldsValue({ pushToMposInhouse: value })}
                                            />
                                        </Form.Item>
                                        <span>{translate.formatMessage(commonMessage.pushToMposInhouse)}</span>
                                    </Flex>
                                </Col>

                                <Col span={6}>
                                    <Flex align="center" gap={12}>
                                        <Form.Item
                                            name="pushToMposImbiss"
                                            valuePropName="checked"
                                            noStyle
                                            required
                                        >
                                            <Switch
                                                disabled={!hasPermission}
                                                onChange={(value) => form.setFieldsValue({ pushToMposImbiss: value })}
                                            />
                                        </Form.Item>
                                        <span>{translate.formatMessage(commonMessage.pushToMposImbiss)}</span>
                                    </Flex>
                                </Col>

                                <Col span={6}>
                                    <Flex align="center" gap={12}>
                                        <Form.Item
                                            name="pushToMposTakeaway"
                                            valuePropName="checked"
                                            noStyle
                                            required
                                        >
                                            <Switch
                                                disabled={!hasPermission}
                                                onChange={(value) => form.setFieldsValue({ pushToMposTakeaway: value })}
                                            />
                                        </Form.Item>
                                        <span>{translate.formatMessage(commonMessage.pushToMposTakeaway)}</span>
                                    </Flex>
                                </Col>

                                <Col span={6}>
                                    <Flex align="center" gap={12}>
                                        <Form.Item
                                            name="pushToMposDelivery"
                                            valuePropName="checked"
                                            noStyle
                                            required
                                        >
                                            <Switch
                                                disabled={!hasPermission}
                                                onChange={(value) => form.setFieldsValue({ pushToMposDelivery: value })}
                                            />
                                        </Form.Item>
                                        <span>{translate.formatMessage(commonMessage.pushToMposDelivery)}</span>
                                    </Flex>
                                </Col>
                            </Row>
                        </fieldset>
                    </Col>
                </Row>

                {hasPermission && (
                    <div className="footer-card-form">
                        <Flex justify="end" gap={16}>
                            <Button htmlType="submit" type="primary" loading={loading} icon={<SaveOutlined />} disabled={!isChanged}>
                                {translate.formatMessage(commonMessage.update)}
                            </Button>
                        </Flex>
                    </div>
                )}
            </div>
        </BaseForm>
    );
};

export default KitplaySettingForm;